<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { faArrowLeft } from '@fortawesome/duotone-regular-svg-icons'
import useSubscriptionsStore from '~/stores/subscriptions'
import useLicenseStore from '~/stores/license'
import Button from '~/components/ui/Button.vue'
import AuSkeletonWrapper from '~/components/ui/AuSkeletonWrapper.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import AuDrawer from '~/components/ui/AuDrawer.vue'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits(['back', 'close'])

const { t } = useI18n()
const {
  hasSubscription,
  trialEndDateMessages,
  showUpgradeFromPlus,
  showUpgradeFromBasic,
  subscriptionsDetails,
  webshopMessages,
} = storeToRefs(useSubscriptionsStore())

const {
  isLoadingLicenses,
  hasLoadedLicenses,
  hasLicenses,
} = storeToRefs(useLicenseStore())
</script>

<template>
  <AuDrawer
    :title="t('settings.drawers.subscription')"
    :open="props.open"
    :backdrop="false"
    center-title
    @close="emit('close')"
  >
    <template #content>
      <Button
        shape="square"
        :aria-label="t('back')"
        size="xl"
        class="absolute top-6 left-6"
        @click="emit('back')"
      >
        <font-awesome-icon :icon="faArrowLeft" />
      </Button>
      <div class="flex flex-col gap-4 py-6">
        <AuSkeletonWrapper v-if="isLoadingLicenses">
          <AuSkeleton height="10rem" />
        </AuSkeletonWrapper>
        <section v-if="hasLoadedLicenses">
          <div v-if="hasLicenses && hasSubscription">
            <p
              class="mb-4 text-xl font-bold"
              v-text="t('subscription.messages.current')"
            />
            <div class="grid size-full grid-cols-2 gap-6">
              <div
                v-for="subscription in subscriptionsDetails"
                :key="subscription.ean"
                class="bg-green-40 flex flex-col gap-4 rounded-lg py-6 text-center text-white"
              >
                <p
                  class="text-2xl font-black"
                  v-text="`${t('subscription.application')} ${t(`metadata.subscriptionGrades.${subscription.grades}`)}`"
                />
                <span
                  v-if="subscription.type"
                  class="px-6 py-1 text-lg font-black uppercase shadow"
                  :class="subscription.type === 'plus' ? 'bg-yellow-pop text-green-40' : 'bg-seagreen-10 text-green-40'"
                  v-text="t(`subscription.type.${subscription.type}`)"
                />
              </div>
            </div>
            <div class="mt-6">
              <p
                v-for="message in trialEndDateMessages"
                :key="message"
              >
                {{ message }}
              </p>
              <p
                v-if="showUpgradeFromBasic"
                v-text="t('subscription.messages.upgrade.fromBasic')"
              />
              <p
                v-if="!showUpgradeFromPlus"
                v-text="t('subscription.messages.upgrade.fromPlus')"
              />
              <p
                v-if="webshopMessages"
                class="my-4"
                v-text="t('subscription.messages.readMore')"
              />
              <div
                v-for="message in webshopMessages"
                :key="message"
                v-dompurify-html="message"
                class="prose prose-a:text-green-5 prose-a:hover:text-green-10 prose-a:focus-visible:ring my-4"
              />
            </div>
          </div>
          <div v-if="!hasLicenses || !hasSubscription">
            <p v-text="t('subscription.messages.missing')" />
          </div>
        </section>
      </div>
    </template>
  </AuDrawer>
</template>
